import React, { useState } from 'react';
import { Users, UserPlus, UserMinus, Send } from 'lucide-react';

const BeteiligteTab = ({ caseId, caseData, onRefresh, token }) => {
  const [showLawyerForm, setShowLawyerForm] = useState(false);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [lawyerData, setLawyerData] = useState({
    email: '',
    name: '',
    phone: '',
    law_firm: ''
  });

  const [customerData, setCustomerData] = useState({
    email: '',
    name: '',
    phone: ''
  });

  const assignLawyer = async () => {
    if (!lawyerData.email || !lawyerData.name) {
      alert('Bitte Email und Name angeben');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-assignment/assign-lawyer/${caseId}?admin_token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lawyerData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Fehler');
      }

      const data = await response.json();
      alert(data.invite_sent ? 'Anwalt zugewiesen und Einladung verschickt' : 'Anwalt zugewiesen');
      setShowLawyerForm(false);
      setLawyerData({ email: '', name: '', phone: '', law_firm: '' });
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const removeLawyer = async () => {
    if (!confirm('Anwalt wirklich entfernen?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-assignment/remove-lawyer/${caseId}?admin_token=${token}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Fehler');

      alert('Anwalt entfernt');
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const assignCustomer = async () => {
    if (!customerData.email || !customerData.name) {
      alert('Bitte Email und Name angeben');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-assignment/assign-customer/${caseId}?admin_token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Fehler');
      }

      alert('Kunde zugewiesen und Einladung verschickt');
      setShowCustomerForm(false);
      setCustomerData({ email: '', name: '', phone: '' });
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resendCustomerInvite = async () => {
    // Re-use assign endpoint
    await assignCustomer();
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Beteiligte verwalten</h2>
        <p className="text-gray-600">Weise Kunde und Anwalt zu, verwalte Zugriffsrechte</p>
      </div>

      {/* Customer */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-brand-green" />
            Kunde
          </h3>
          {!caseData.customer_id && (
            <button
              onClick={() => setShowCustomerForm(!showCustomerForm)}
              className="px-4 py-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Kunde hinzufügen
            </button>
          )}
        </div>

        {caseData.customer_id ? (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="font-semibold text-gray-900">{caseData.customer_name}</p>
            <p className="text-sm text-gray-600">{caseData.customer_email}</p>
            <button
              onClick={resendCustomerInvite}
              disabled={loading}
              className="mt-3 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1 transition-colors"
            >
              <Send className="w-3 h-3" />
              Einladung erneut senden
            </button>
          </div>
        ) : showCustomerForm ? (
          <div className="space-y-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Email *</label>
              <input
                type="email"
                value={customerData.email}
                onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green"
                placeholder="kunde@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Name *</label>
              <input
                type="text"
                value={customerData.name}
                onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green"
                placeholder="Max Mustermann"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Telefon</label>
              <input
                type="text"
                value={customerData.phone}
                onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green"
                placeholder="+49 ..."
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={assignCustomer}
                disabled={loading}
                className="px-4 py-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Sende...' : 'Zuweisen & Einladung senden'}
              </button>
              <button
                onClick={() => setShowCustomerForm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-xl">
            <p className="text-gray-600 text-sm">Noch kein Kunde zugewiesen</p>
          </div>
        )}
      </div>

      {/* Lawyer */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-brand-green" />
            Anwalt
          </h3>
          {!caseData.lawyer_id ? (
            <button
              onClick={() => setShowLawyerForm(!showLawyerForm)}
              className="px-4 py-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Anwalt hinzufügen
            </button>
          ) : (
            <button
              onClick={removeLawyer}
              disabled={loading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2 transition-colors"
            >
              <UserMinus className="w-4 h-4" />
              Anwalt entfernen
            </button>
          )}
        </div>

        {caseData.lawyer_id ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="font-semibold text-gray-900">{caseData.lawyer_name}</p>
            <p className="text-sm text-gray-600">{caseData.lawyer_email}</p>
          </div>
        ) : showLawyerForm ? (
          <div className="space-y-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Email *</label>
              <input
                type="email"
                value={lawyerData.email}
                onChange={(e) => setLawyerData({ ...lawyerData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green"
                placeholder="anwalt@kanzlei.de"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Name *</label>
              <input
                type="text"
                value={lawyerData.name}
                onChange={(e) => setLawyerData({ ...lawyerData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green"
                placeholder="Dr. Anna Schmidt"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Kanzlei</label>
              <input
                type="text"
                value={lawyerData.law_firm}
                onChange={(e) => setLawyerData({ ...lawyerData, law_firm: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green"
                placeholder="Kanzlei Schmidt & Partner"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Telefon</label>
              <input
                type="text"
                value={lawyerData.phone}
                onChange={(e) => setLawyerData({ ...lawyerData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green"
                placeholder="+49 ..."
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={assignLawyer}
                disabled={loading}
                className="px-4 py-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Sende...' : 'Zuweisen & Einladung senden'}
              </button>
              <button
                onClick={() => setShowLawyerForm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-xl">
            <p className="text-gray-600 text-sm">Noch kein Anwalt zugewiesen</p>
          </div>
        )}
      </div>

      {/* Partner (read-only) */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-brand-green" />
          Partner (Werkstatt)
        </h3>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <p className="font-semibold text-gray-900">{caseData.partner_company_name || 'Unbekannt'}</p>
          <p className="text-sm text-gray-600">Partner-ID: {caseData.partner_id}</p>
        </div>
      </div>
    </div>
  );
};

export default BeteiligteTab;
